import * as React from "react";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { DrawdownIncomeAmendmentModel } from "../../../../../../../Wrap/Models/Models/Processes/Drawdown/DrawdownIncomeAmendmentModel";
import { MoneyHelper } from "../../../../../../Shared/Common/MoneyHelper";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { SubAccountTypeHelper } from "../../../Common/Helpers/SubAccountTypeHelper";
import { processTypeEnum } from "../../../Common/Resource/Contents";
import {
    getSellableAssets,
    getUpdatedAssetsToSell,
} from "../../../Common/SIPP/DrawdownDetails/Helper/DrawdownDetailHelper";
import { ChooseAssetsToSellAssets } from "../../../Common/SIPP/DrawdownDetails/Store/DrawdownCalculationStoreStatic";
import { CappedFlexiSection } from "../../../Common/SIPP/IncomePaymentDetails/CappedFlexiSection";
import { calculateAnnualIncomeAmount, frequencyMap } from "../../../Common/SIPP/IncomePaymentDetails/Helper/IncomePaymentHelper";
import IncomeBankDetails from "../../../Common/SIPP/IncomePaymentDetails/IncomeBankDetails";
import IncomeRequired from "../../../Common/SIPP/IncomePaymentDetails/IncomeRequired";
import RegularIncomePayment from "../../../Common/SIPP/IncomePaymentDetails/RegularIncomePayment";
import {
    incomePaymentDetailsLabels,
    incomePaymentDetailsMessages,
} from "../../../Common/SIPP/IncomePaymentDetails/Resource/Content";
import { ITFCIncome } from "../../../EditIncomeWithdrawalsWizard/Components/IncomePaymentDetails/ITFCIncome";
import { RestrictionHelper } from "../../../../../../Shared/Common/RestrictionHelper";
import { RestrictionStore } from "../../../Common/Restrictions/Variable";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { RestrictionAction } from "../../../Common/Restrictions/RestrictionAction";
import { PensionContributionModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContributionModel";
import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { PaymentMethod } from "../../../../../../../Wrap/Constants/Enums/Domain/PaymentMethod";
import { ProductType } from "../../../../../../../Wrap/Constants/Enums/Domain/ProductType";
import { InvestmentAllocation } from "../../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { IJsonDate } from "../../../../../../../Wrap/System/IJsonDate";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { BusinessDaysAction } from "../../../../Common/BusinessDays/Actions/BusinessDaysAction";
import { Frequency } from "../../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import _ from "lodash";
import { IncomePaymentDestination } from "../../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentDestination";

interface IIncomePaymentDetailsProps extends IProcessComponentProps {
    data: PensionContributionModel;
    isLoading: boolean;
    extraInfo?: any;
    processType: processTypeEnum;
}

interface IIncomePaymentDetailsState {
    assets: ChooseAssetsToSellAssets[];
    isIncomeRequired: boolean;
    assetDictionary: Map<number, ProductType>;
    regularIncomeFirstPaymentDateAtLeast: IJsonDate;
}

export class IncomePaymentDetails extends ProcessComponent<IIncomePaymentDetailsProps, IIncomePaymentDetailsState> {
    _isMounted = false;

    constructor(props: any) {
        super(props);
        this.state = {
            assets: [],
            isIncomeRequired: false,
            assetDictionary: null,
            regularIncomeFirstPaymentDateAtLeast: null
        };
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;
        super.componentDidMount();

        const data = this.getDrawdownIncomeAmendmentModel();
        this.getSellableAssets();
        if (!isNullOrEmpty(data?.DrawdownIncomeAmendmentInstruction?.IncomeDisinvestmentAllocations)) {
            this.props.onChange({ IsAcrossAllAssets: false });
        }
        if (RestrictionStore.getData() == null) {
            if (!CommonHelper.isNullOrEmpty(getQueryParameters()["subaccounthierarchyid"])) {
                RestrictionAction.getRestrictionByHierarchyId(getQueryParameters()["subaccounthierarchyid"]);
            } else if (!CommonHelper.isNullOrEmpty(getQueryParameters()["hierarchyid"])) {
                RestrictionAction.getRestrictionByHierarchyId(getQueryParameters()["hierarchyid"]);
            }
        }

        if (data?.DrawdownIncomeAmendmentInstruction?.Income?.PaymentMethod) {
            this.handleChange({ PaymentMethod: data?.DrawdownIncomeAmendmentInstruction?.Income?.PaymentMethod }, "Income");
        }
    }

    private getDrawdownIncomeAmendmentModel(): DrawdownIncomeAmendmentModel {
        const data = this.props.data
        return data?.Contribution?.DrawdownIncomeAmendmentModel;
    }

    private getSellableAssets = () => {
        const params = getQueryParameters();
        const hierarchyId = params["hierarchyid"];
        SubAccountTypeHelper.getSubAccountHierarchyId(hierarchyId, SubAccountType.Npr, (subAccountHierarchyId) => {
            getSellableAssets(SubAccountType.Npr, hierarchyId, subAccountHierarchyId, (data) => {
                if (this._isMounted) {
                    const updateAssets = getUpdatedAssetsToSell([], 0, data?.assetsToSell);
                    this.setState({ assets: updateAssets, assetDictionary: this.constructAssetDictionary(updateAssets) });
                }

            });
        });
    };

    private constructAssetDictionary(assetArray: ChooseAssetsToSellAssets[]): Map<number, ProductType> {
        let result = new Map<number, ProductType>();
        for (let asset of assetArray) {
            if (!(asset.Id in result)) {
                result[asset.Id] = asset.ProductType;
            }
        }
        return result;
    }

    private checkDisinvestmentHasOnlyCashOrEmpty(disinvestmentAllocations: Array<InvestmentAllocation>): boolean {
        if (!disinvestmentAllocations) {
            return true;
        }

        const { assetDictionary } = this.state;

        return !disinvestmentAllocations.some(allocation => allocation?.Percentage > 0 && allocation?.ProductId > 0 && assetDictionary?.[allocation?.ProductId] != ProductType.Cash);
    }

    private async daysAfterNow(days): Promise<Date> {
        const resultDate = await this.getBusinessDays(new Date(Date.now()), true, days);
        if (resultDate.getDate() > 28) {
            resultDate.setMonth(resultDate.getMonth() + 1);
            resultDate.setDate(1);
        }
        return resultDate;
    }

    private getBusinessDays(baseDate, future, numberOfDays): Promise<Date> {
        return new Promise((resolve) => {
            BusinessDaysAction.GetBusinessDays(baseDate, future, numberOfDays, true, (d) => {
                resolve(JsonDateParser.toDateNoOffset(d));
            });
        });
    }

    private getDrawdownIncomeModelProps = (data: DrawdownIncomeAmendmentModel, onChange) => {
        return {
            data: data?.DrawdownIncomeAmendmentInstruction,
            onChange: (v) => this.handleChange(v),
            onRef: (ref) => this.appendChild(ref),
            showErrorMsgPopUp: this.props.showErrorMsgPopUp,
            isReview: this.props.isReview,
        };
    };

    private onIncomeReviewChange(changes) {
        const { data, onChange } = this.props;
        this.onDataChange(
            (changes) => this.onDataChange(onChange, changes, data, "Contribution"),
            changes,
            data.Contribution,
            "DrawdownIncomeReviewDetails"
        )
    }

    private async handleChange(changes, key?: string) {
        const { data, onChange } = this.props;
        const model = this.getDrawdownIncomeAmendmentModel();

        var updatedModel = _.cloneDeep(model);
        this.applyChangesToClonedModel(changes, key ? updatedModel.DrawdownIncomeAmendmentInstruction[key] : updatedModel.DrawdownIncomeAmendmentInstruction);

        if (key == "Income" && changes["Frequency"]) {
            this.handleFrequencyChange(changes, updatedModel);
        }

        this.onDataChange(
            (changes) => {
                this.onDataChange(onChange, changes, data, "Contribution");
            },
            updatedModel,
            data.Contribution,
            "DrawdownIncomeAmendmentModel"
        );
    }

    private applyChangesToClonedModel(changes, model) {
        for (const item in changes) {
            model[item] = JSON.parse(JSON.stringify(changes[item]));
        }
    }

    private handleFrequencyChange(changes, updatedModel: DrawdownIncomeAmendmentModel) {
        const annualIncome = updatedModel?.DrawdownIncomeAmendmentInstruction?.Income;
        let grossIncomePerPayment = changes['GrossIncomePerPayment'] ?? annualIncome?.GrossIncomePerPayment;
        let incomeFrequency = changes['Frequency'] ?? annualIncome?.Frequency;

        if (grossIncomePerPayment?.Amount != null && grossIncomePerPayment?.Amount != undefined &&
            incomeFrequency != null && incomeFrequency != undefined) {

            updatedModel.DrawdownIncomeAmendmentInstruction.Income.AnnualAmount = {
                Amount: calculateAnnualIncomeAmount(grossIncomePerPayment.Amount, incomeFrequency)
            };
        }

        // calculate maximum per payment according to the frequency
        const currentFrequency = updatedModel?.DrawdownIncomeAmendmentInstruction?.Income?.Frequency || Frequency.Yearly
        const currentFrequencyValue = frequencyMap[currentFrequency] as number
        const nextFrequencyValue = frequencyMap[changes.Frequency] as number
        if (updatedModel?.DrawdownIncomeAmendmentInstruction?.CappedArrangements?.length) {
            updatedModel.DrawdownIncomeAmendmentInstruction.CappedArrangements.forEach((item) => item.MaximumIncomePaymentForRemainderOfPensionYear
                = Number(
                    (currentFrequencyValue * (item?.MaximumIncomePaymentForRemainderOfPensionYear || 0) / nextFrequencyValue)
                        .toFixed(2)))
        }
        if (updatedModel?.DrawdownIncomeAmendmentInstruction?.FlexiArrangements?.length) {
            updatedModel.DrawdownIncomeAmendmentInstruction.FlexiArrangements.forEach((item) => {
                item.MaximumIncomePerPayment
                    = Number(
                        (currentFrequencyValue * (item?.MaximumIncomePerPayment || 0) / nextFrequencyValue)
                            .toFixed(2))
            })
        }
        if (updatedModel?.DrawdownIncomeAmendmentInstruction?.Income?.MaximumIncomePerPayment) {
            const currentValue = updatedModel.DrawdownIncomeAmendmentInstruction?.Income?.MaximumIncomePerPayment || MoneyHelper.getDefaultMoney()
            updatedModel.DrawdownIncomeAmendmentInstruction.Income.MaximumIncomePerPayment.Amount
                = Number(((currentFrequencyValue * currentValue.Amount) / nextFrequencyValue).toFixed(2))
        }
        updatedModel.DrawdownIncomeAmendmentInstruction.Income.TakeFullValue = false
    }

    public render(): JSX.Element {
        const { onChange, extraInfo, processType } = this.props;
        const data = this.getDrawdownIncomeAmendmentModel();
        const pensionBase = this.props.data?.Contribution;
        let isRestrictionActive = false;
        let restrictedMessage = "An account with suspended or restricted activities cannot edit income payments";
        if (RestrictionHelper.shouldHandleRestriction(processType)) {
            isRestrictionActive = RestrictionStore.WithdrawDisabled();
        }
        const existingArrangements = {
            CappedModels: data?.DrawdownIncomeAmendmentInstruction?.CappedArrangements,
            FlexiModels: data?.DrawdownIncomeAmendmentInstruction?.FlexiArrangements
        }

        const hasBankDestination = data?.DrawdownIncomeAmendmentInstruction?.Income?.IncomePaymentAllocations?.some(alloc => alloc.Destination == IncomePaymentDestination.BankAccount)

        return (
            <PartLoading isLoading={this.props.isLoading}>
                <h2>Income payment details</h2>
                {isRestrictionActive && <Alert alertType={AlertType.Error} message={restrictedMessage} />}
                <ITFCIncome
                    {...this.getDrawdownIncomeModelProps(data, onChange)}
                    subaccountHierarchyid={getQueryParameters()["subaccounthierarchyid"]}
                />
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label={incomePaymentDetailsLabels.needToProvideAnIncome}
                    disabled={isRestrictionActive}
                    selected={
                        data?.DrawdownIncomeAmendmentInstruction?.Income?.TaxableIncomeRequiredInAddition || false
                    }
                    options={[true, false]}
                    getText={(v) => (v ? "Yes" : "No")}
                    onChange={(v) => {
                        if (!v) {
                            this.handleChange({ IncomeDisinvestmentAllocations: [], TaxableIncomeRequiredInAddition: v }, "Income");
                        } else {
                            this.handleChange({ TaxableIncomeRequiredInAddition: v }, "Income")
                        }
                    }}
                />
                {data?.DrawdownIncomeAmendmentInstruction?.Income?.TaxableIncomeRequiredInAddition && (
                    <>
                        <IncomeRequired
                            data={data?.DrawdownIncomeAmendmentInstruction?.Income}
                            onChange={(changes) => {
                                this.handleChange(changes, "Income");
                            }}
                            labels={incomePaymentDetailsLabels}
                            messages={incomePaymentDetailsMessages}
                            onRef={(ref) => this.appendChild(ref)}
                            processType={processTypeEnum.WhatIfTool}
                            drawdownType={DrawdownType.Flexible}
                            pensionTransfer={[]}
                            pensionBase={pensionBase}
                            isITFC={false}
                            hasCappedArrangement={!isNullOrEmpty(pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.CappedArrangements || [])}
                        />
                        {data?.DrawdownIncomeAmendmentInstruction?.Income?.TargetSpecificArrangements && (
                            <CappedFlexiSection
                                data={existingArrangements}
                                onChange={(changes, type) => {
                                    const convertedChanges = type === "CappedModels" ? { CappedArrangements: changes?.CappedModels } : {FlexiArrangements: changes?.FlexiModels }
                                    const convertedKey =  type === "CappedModels" ? "CappedArrangements" : "FlexiArrangements"
                                    this.handleChange(convertedChanges[convertedKey], convertedKey);
                                }}
                                income={data?.DrawdownIncomeAmendmentInstruction?.Income}
                                onIncomeChange={(changes) => this.handleChange(changes, "Income")}
                                showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                                onRef={(ref) => this.appendChild(ref)}
                                drawdownType={DrawdownType.Flexible}
                                drawdownIncomeReview={this.props.data.Contribution?.DrawdownIncomeReviewDetails}
                                onIncomeReviewChange={(changes) => this.onIncomeReviewChange(changes)}
                                processType={processTypeEnum.WhatIfTool}
                            />
                        )}
                        <RegularIncomePayment
                            data={data?.DrawdownIncomeAmendmentInstruction?.Income}
                            onChange={(changes) => {
                                this.handleChange(changes, "Income");
                            }}
                            labels={incomePaymentDetailsLabels}
                            messages={incomePaymentDetailsMessages}
                            shouldHidePaymentMethod={!hasBankDestination}
                            onRef={(ref) => this.appendChild(ref)}
                            drawdownType={DrawdownType.Flexible}
                            pensionBase={pensionBase}
                            isITFC={false}
                            subaccountHierarchyid={getQueryParameters()["subaccounthierarchyid"]}
                        />
                        <hr />
                        <IncomeBankDetails
                            data={data?.DrawdownIncomeAmendmentInstruction?.Income?.IncomePaymentAllocations || []}
                            onChange={(value) => {
                                let parentIncome = data?.DrawdownIncomeAmendmentInstruction?.Income;
                                if (parentIncome) {
                                    parentIncome.IncomePaymentAllocations = value;
                                    this.handleChange(parentIncome, "Income");
                                }

                            }}
                            onRef={(ref) => this.appendChild(ref)}
                            allBankAccounts={extraInfo?.AllBankAccounts ?? []}
                            showErrorMsgPopUp={(msg, isShow) => this.props.showErrorMsgPopUp(msg, isShow)}
                            showPropotionOfIncome={
                                data?.DrawdownIncomeAmendmentInstruction?.Income?.TaxableIncomeRequiredInAddition
                            }
                        />
                    </>
                )}
                {!data?.DrawdownIncomeAmendmentInstruction?.Income?.TaxableIncomeRequiredInAddition && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={"It will cancel the active income drawdown instruction"}
                    />
                )}
            </PartLoading>
        );
    }

}
